import React from 'react'
import ErrorPage from 'next/error'
import { groq } from 'next-sanity'
import { useRouter } from 'next/router'
import { usePreviewSubscription } from '@/lib/sanity'
import { getClient } from '@/lib/sanity.server'
import { Layout } from '@/components/layout/landing-page'
import { SEO } from '@/components/seo'
import { Loader } from '@/components/shared/loader'

function filterDataToSingleItem(data, preview) {
  if (!Array.isArray(data)) {
    return data
  }

  if (data.length === 1) {
    return data[0]
  }

  if (preview) {
    return data.find((item) => item._id.startsWith(`drafts.`)) || data[0]
  }

  return data[0]
}

export async function getStaticProps({ params, preview = false }) {

  const query = groq`*[_type in ["landingPage", "siteSettings", "navigation", "course", "fieldOfStudy", "instructor"]][0]{
    "siteSettings": *[_type == "siteSettings"][0],
    "superPages": *[_type == "landingPage" && slug.current == "our-instructors"]
    {
      ...,
      "slug": slug.current,
    },
    "instructors": *[_type == "instructor"] | order(lastName asc)
    {
      _id,
      "slug": slug.current,
      lastName,
      firstName,
      professionalTitle,
      image,
      sortOrder
    },
    "siteNav": *[_type == "navigation" && _id == "mainNav"][0]["links"],
    "topNav": *[_type == "navigation" && _id == "topNav"][0]["links"],
    "topFooterNav": *[_type == "navigation" && _id == "topFooterNav"][0]["links"],
    "bottomFooterNav": *[_type == "navigation" && _id == "bottomFooterNav"][0]["links"],
  }`

  const queryParams = { slug: "our-instructors" }
  const data = await getClient(preview).fetch(query, queryParams)

  // Escape hatch, if our query failed to return data
  if (!data) return { notFound: true }

  // Helper function to reduce all returned documents down to just one
  const page = filterDataToSingleItem(data, preview)

  return {
    props: {
      // Pass-down the preview context
      preview,
      // Pass-down the initial content, and our query
      data: { page, query, queryParams },
    },
    revalidate: 60
  }
}

/**
 * The `usePreviewSubscription` takes care of updating
 * the preview content on the client-side
 */
export default function Page({ data, preview }) {
  // const { asPath } = useRouter()
  const { data: previewData } = usePreviewSubscription(data?.query, {
    params: data?.queryParams ?? {},
    // The hook needs to know what we started with, to return it immediately
    // This is what it's important to fetch draft content server-side!
    initialData: data?.page,
    // The passed-down preview context determines whether this function does anything
    enabled: preview,
  })

  const page = filterDataToSingleItem(previewData, preview)

  // Notice the optional?.chaining conditionals wrapping every piece of content. This is extremely important as you can't ever rely on a single field of data existing when Editors are creating new documents. It'll be completely blank when they start!

  const superPage = page?.superPages[0] || null

  const siteSettings = page?.siteSettings || null
  const siteNav = page?.siteNav || null
  const topNav = page?.topNav || null
  const topFooterNav = page?.topFooterNav || null
  const bottomFooterNav = page?.bottomFooterNav || null
  const contentComponents = page?.superPages[0]?.contentComponents || null
  const instructors = page?.instructors || []

  const view = {
    "instructorsPageView": [...instructors]
  }

  const router = useRouter()
  if (!router.isFallback && !superPage?.slug) {
    return <ErrorPage statusCode={404} />
  }
  if (router.isFallback) {
    return <Loader />
  }
  return (<>
    <SEO page={superPage} site={siteSettings} path={superPage?.slug} />
    <Layout preview={preview} asPath={superPage?.slug} siteNav={siteNav} topNav={topNav} topFooterNav={topFooterNav} bottomFooterNav={bottomFooterNav} siteSettings={siteSettings} contentComponents={contentComponents} view={view} />
  </>)
}
